import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { uploadToDrive, listDriveFiles, createDriveFolder } from '../../lib/google';
import { B, FONT } from '../../lib/constants';

const DRIVE_FOLDERS = {
  1: '1Ww2LuFXi8PxV4pVMpoIQekhzA0Jz2FYN',
  2: '1oRoS-NXvBdyQkru3hVLe7i7HHLy8X7o5',
  3: '1PH2p8eivHFJLv3Y-DcAMy3TOiov5nt2H',
  4: '130B7I0_sLP2vcggXBIcuhxX24XR_aMLa',
};

const TAGS = ['Foundation','Framing','Roofing','Plumbing','Electrical','HVAC','Drywall','Tile','Flooring','Cabinets','Trim','Exterior','Interior','Progress','Issue','Other'];

const getOrCreateFolder = async (parentId, name) => {
  try {
    const files = await listDriveFiles(parentId);
    const existing = files.find(f => f.mimeType === 'application/vnd.google-apps.folder' && f.name === name);
    if (existing) return existing.id;
    const created = await createDriveFolder(name, parentId);
    return created.id;
  } catch(e) {
    return parentId;
  }
};

export default function AddPhotoModal({ projects, defaultProjectId, onClose }) {
  const [projectId, setProjectId] = useState(defaultProjectId || projects[0]?.id);
  const [photos, setPhotos] = useState([]);
  const [batchTag, setBatchTag] = useState('');
  const [batchCaption, setBatchCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const cameraInputRef = useRef();
  const fileInputRef = useRef();

  const addFiles = (files) => {
    const newPhotos = Array.from(files).map(file => ({
      file, previewUrl: URL.createObjectURL(file),
      uploading: false, done: false, error: null,
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (i) => setPhotos(prev => prev.filter((_, idx) => idx !== i));

  const handleUploadAll = async () => {
    if (!batchTag) { alert('Please select a label before uploading.'); return; }
    setUploading(true);
    const rootFolderId = DRIVE_FOLDERS[parseInt(projectId)];
    const folderId = await getOrCreateFolder(rootFolderId, batchTag);
    const dateStr = new Date().toISOString().split('T')[0];

    for (let i = 0; i < photos.length; i++) {
      const p = photos[i];
      if (p.done) continue;
      setPhotos(prev => prev.map((x, idx) => idx === i ? { ...x, uploading: true } : x));
      try {
        const fileName = dateStr + '_' + batchTag + '_' + (i + 1) + '.jpg';
        const result = await uploadToDrive(p.file, folderId, fileName);
        if (result.id) {
          await supabase.from('project_photos').insert({
            project_id: parseInt(projectId),
            drive_file_id: result.id,
            file_name: fileName,
            tag: batchTag,
            caption: batchCaption || null,
            drive_url: result.webViewLink || null,
          });
        }
        setPhotos(prev => prev.map((x, idx) => idx === i ? { ...x, done: true, uploading: false } : x));
      } catch(e) {
        setPhotos(prev => prev.map((x, idx) => idx === i ? { ...x, error: 'Failed', uploading: false } : x));
      }
    }
    setUploading(false);
    setAllDone(true);
  };

  const pendingCount = photos.filter(p => !p.done).length;
  const doneCount = photos.filter(p => p.done).length;
  const title = allDone ? 'Done' : (photos.length > 0 ? 'Add Photos (' + photos.length + ')' : 'Add Photos');
  const uploadLabel = uploading ? 'Uploading...' : (!batchTag ? 'Select a label first' : 'Upload ' + pendingCount + ' Photo' + (pendingCount !== 1 ? 's' : '') + ' to ' + batchTag);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', fontFamily: FONT }}>
      <div style={{ background: B.dark, borderRadius: '20px 20px 0 0', width: '100%', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 12px' }}>
          <div style={{ color: B.white, fontSize: 18, fontWeight: 800 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 24, cursor: 'pointer', padding: 0 }}>x</button>
        </div>

        {allDone ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ color: B.white, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{doneCount} photo{doneCount !== 1 ? 's' : ''} uploaded</div>
            <div style={{ color: B.muted, fontSize: 12, marginBottom: 24 }}>Saved to Drive / {batchTag}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setPhotos([]); setAllDone(false); setBatchTag(''); setBatchCaption(''); }}
                style={{ flex: 1, background: B.slate, color: B.white, border: 'none', borderRadius: 12, padding: 14, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: FONT }}>
                + Add More
              </button>
              <button onClick={onClose}
                style={{ flex: 1, background: B.goldlt, color: B.black, border: 'none', borderRadius: 12, padding: 14, cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '0 20px 20px' }}>

            {projects.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <select value={projectId} onChange={e => setProjectId(e.target.value)}
                  style={{ width: '100%', background: B.mid, border: '1px solid ' + B.slate, borderRadius: 10, padding: '10px 12px', color: B.white, fontSize: 14, fontFamily: FONT }}>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}

            <div style={{ background: B.navy, borderRadius: 12, padding: 14, marginBottom: 16, border: '1px solid ' + (batchTag ? B.gold : B.slate) }}>
              <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Label — applies to all photos</div>
              <select value={batchTag} onChange={e => setBatchTag(e.target.value)}
                style={{ width: '100%', background: B.mid, border: '1px solid ' + (batchTag ? B.gold : B.slate), borderRadius: 8, padding: '10px 12px', color: batchTag ? B.white : B.muted, fontSize: 14, fontFamily: FONT, marginBottom: 8 }}>
                <option value=''>Select a label...</option>
                {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={batchCaption} onChange={e => setBatchCaption(e.target.value)}
                placeholder="Caption (optional)"
                style={{ width: '100%', background: B.mid, border: '1px solid ' + B.slate, borderRadius: 8, padding: '9px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
            </div>

            {photos.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 16 }}>
                {photos.map((p, i) => (
                  <div key={i} style={{ aspectRatio: '1', position: 'relative', borderRadius: 10, overflow: 'hidden', background: B.slate }}>
                    <img src={p.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {p.done && <div style={{ position: 'absolute', inset: 0, background: 'rgba(76,175,61,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>✓</div>}
                    {p.uploading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>...</div>}
                    {p.error && <div style={{ position: 'absolute', inset: 0, background: 'rgba(231,76,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>x</div>}
                    {!p.done && !p.uploading && (
                      <button onClick={() => removePhoto(i)}
                        style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', color: '#fff', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>x</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <button onClick={() => cameraInputRef.current?.click()}
                style={{ flex: 1, background: B.navy, border: '1px solid ' + B.gold, borderRadius: 12, padding: '14px', cursor: 'pointer', color: B.goldlt, fontSize: 13, fontWeight: 700, fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>📷</span> Camera
              </button>
              <button onClick={() => fileInputRef.current?.click()}
                style={{ flex: 1, background: B.navy, border: '1px solid ' + B.slate, borderRadius: 12, padding: '14px', cursor: 'pointer', color: B.white, fontSize: 13, fontWeight: 700, fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>🖼️</span> Library
              </button>
            </div>

            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
            <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

            {photos.length > 0 && (
              <button onClick={handleUploadAll} disabled={uploading || pendingCount === 0 || !batchTag}
                style={{ width: '100%', background: (pendingCount > 0 && batchTag) ? B.goldlt : B.slate, color: (pendingCount > 0 && batchTag) ? B.black : B.muted, border: 'none', borderRadius: 12, padding: 15, cursor: (pendingCount > 0 && batchTag) ? 'pointer' : 'default', fontSize: 15, fontWeight: 800, fontFamily: FONT }}>
                {uploadLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
